import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ExternalLink } from 'lucide-react';
import {
  type AffiliateProvider,
  type AffiliateType,
  generateAffiliateUrl,
  getAffiliateCtaText,
  isAffiliateEnabled,
} from '@/lib/affiliateConfig';
import { useTestVariant, useTrackConversion } from '@/lib/abTesting';

interface AffiliateCtaProps {
  provider: AffiliateProvider;
  occupation?: string;
  location?: string;
  type?: AffiliateType;
  className?: string;
  searchQuery?: string; // For education platforms or Amazon
  additionalParams?: Record<string, string>; // For custom tracking params
  position?: 'top' | 'bottom' | 'middle'; // Position on page (for A/B testing)
  variant?: 'button' | 'banner' | 'inline'; // Design variant (for A/B testing)
  copyVariant?: 'default' | 'benefit-focused' | 'urgency-based'; // Copy variant (for A/B testing)
}

const AffiliateCta = ({ 
  provider, 
  occupation = '', 
  location = '', 
  type = 'job-search',
  className = '',
  searchQuery,
  additionalParams,
  position,
  variant,
  copyVariant,
}: AffiliateCtaProps) => {
  
  // A/B testing: Get variant from test if not explicitly provided
  const placementVariant = useTestVariant('affiliate-cta-placement') || position || 'bottom';
  const designVariant = useTestVariant('affiliate-cta-design') || variant || 'button';
  const copyTestVariant = useTestVariant('affiliate-cta-copy') || copyVariant || 'default';
  const trackConversion = useTrackConversion();

  // Generate affiliate URL with proper tracking
  const affiliateUrl = generateAffiliateUrl(provider, {
    occupation,
    location,
    type,
    searchQuery,
    additionalParams,
  });

  // Get base CTA text from config
  let ctaText = getAffiliateCtaText(provider, occupation, type);
  
  // Apply copy variant
  if (copyTestVariant === 'benefit-focused') {
    ctaText = `Find ${occupation || 'Jobs'} with Better Benefits - ${ctaText}`;
  } else if (copyTestVariant === 'urgency-based') {
    ctaText = `Limited Time: ${ctaText} - Apply Now!`;
  }

  // Check if affiliate is enabled (for analytics)
  const isEnabled = isAffiliateEnabled(provider);

  const handleClick = () => {
    // Track affiliate click for analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'affiliate_click', {
        provider,
        occupation,
        location,
        type,
        affiliate_enabled: isEnabled,
        event_category: 'revenue',
        placement: placementVariant,
        design: designVariant,
        copy_variant: copyTestVariant,
      });
    }

    // Track A/B test conversion
    trackConversion('affiliate-cta-placement', 'click', undefined);
    trackConversion('affiliate-cta-design', 'click', undefined);
    trackConversion('affiliate-cta-copy', 'click', undefined);
  };

  // Render based on design variant
  if (designVariant === 'banner') {
    return (
      <Card className={`bg-gradient-to-r from-[hsl(var(--wage-accent))] to-[hsl(var(--wage-accent)/0.8)] border-0 ${className}`}>
        <CardContent className="p-4">
          <a 
            href={affiliateUrl} 
            target="_blank" 
            rel="noopener noreferrer nofollow sponsored"
            onClick={handleClick}
            className="flex items-center justify-between gap-4 text-white hover:opacity-90 transition-opacity"
          >
            <div className="flex-1">
              <p className="font-semibold text-lg">{ctaText}</p>
              <p className="text-sm opacity-90">Click to explore opportunities</p>
            </div>
            <ExternalLink className="h-5 w-5 shrink-0" />
          </a>
        </CardContent>
      </Card>
    );
  }

  if (designVariant === 'inline') {
    return (
      <a 
        href={affiliateUrl} 
        target="_blank" 
        rel="noopener noreferrer nofollow sponsored"
        onClick={handleClick}
        className={`inline-flex items-center gap-2 text-primary hover:text-primary/80 underline ${className}`}
      >
        <span>{ctaText}</span>
        <ExternalLink className="h-4 w-4" />
      </a>
    );
  }

  // Default: button style
  return (
    <Button 
      asChild
      variant="outline"
      className={`
        w-full 
        bg-gradient-to-r 
        from-[hsl(var(--wage-accent))] 
        to-[hsl(var(--wage-accent)/0.8)] 
        hover:from-[hsl(var(--wage-accent)/0.9)] 
        hover:to-[hsl(var(--wage-accent)/0.7)]
        text-white 
        border-0
        transition-all
        duration-200
        hover:shadow-lg
        hover:scale-105
        ${className}
      `}
      onClick={handleClick}
    >
      <a 
        href={affiliateUrl} 
        target="_blank" 
        rel="noopener noreferrer nofollow sponsored"
        className="flex items-center justify-center gap-2 w-full min-w-0 truncate text-white"
      >
        <span className="truncate text-white">{ctaText}</span>
        <ExternalLink className="h-4 w-4 shrink-0 text-white" />
      </a>
    </Button>
  );
};

export default AffiliateCta;